import { IInputStepSeedDress, SeedDressRateMode } from 'interfaces/IInputStep';

import { SeedDressDraft, StepSelectorOption } from './stepConfig';

export const DEFAULT_SEED_DRESS_DRAFT: SeedDressDraft = {
  rateMode: SeedDressRateMode.Per100Kg,
  rate: null,
  unit: null,
  cost: null,
  linkedStepId: null,
};

/** A fresh draft from the product's catalogue defaults (/100kg, unlinked). */
export const draftFromProductDefaults = (
  product: StepSelectorOption
): SeedDressDraft => ({
  rateMode: SeedDressRateMode.Per100Kg,
  rate: product.rate ?? null,
  unit: product.unit ?? null,
  cost: product.cost ?? null,
  linkedStepId: null,
});

/** The values staged in the current picker session, for Change Details. */
export const draftFromStagedSeedDress = (
  staged: StepSelectorOption
): SeedDressDraft => ({
  rateMode: staged.seedDressRateMode ?? SeedDressRateMode.Per100Kg,
  rate: staged.rate ?? null,
  unit: staged.unit ?? null,
  cost: staged.cost ?? null,
  linkedStepId: staged.linkedSeedStepId ?? null,
});

/** The saved step's current values, for editing an existing Seed Treatment. */
export const draftFromSeedDressStep = (
  step: IInputStepSeedDress
): SeedDressDraft => ({
  rateMode: step.rateMode,
  rate: step.rate,
  unit: step.appUnit,
  cost: step.cost,
  linkedStepId: step.linkedStepId,
});

const effectiveLink = (draft: SeedDressDraft) =>
  draft.rateMode === SeedDressRateMode.Per100Kg ? draft.linkedStepId : null;

/** Same values as far as a saved step goes (the seed link only counts in /100kg mode). */
export const isSameSeedDressDraft = (
  a: SeedDressDraft,
  b: SeedDressDraft
): boolean =>
  a.rateMode === b.rateMode &&
  a.rate === b.rate &&
  a.cost === b.cost &&
  (a.unit?.globalId ?? null) === (b.unit?.globalId ?? null) &&
  effectiveLink(a) === effectiveLink(b);

/** Applies a draft to the product; the seed link only survives in /100kg mode. */
export const buildSeedDressSelection = (
  product: StepSelectorOption,
  draft: SeedDressDraft
): StepSelectorOption => ({
  ...product,
  rate: draft.rate,
  unit: draft.unit,
  cost: draft.cost,
  linkedSeedStepId: effectiveLink(draft),
  seedDressRateMode: draft.rateMode,
});
